import { Card } from '@amelio/ui-library';

import { REPORT_SECTION_TITLE } from '../reportStyles';

import type { ReportProficiencyLevel } from '@/types/reports';

import { useAmelioTranslations } from '@/hooks';
import { ProficiencyLegendItem } from '@/pages/PerformanceProfile/components/SkillsetAssessment/ProficiencyLegendItem';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevelsWorstFirst } from '@/utils/proficiencyLevel';

interface LevelLegendProps {
    levels: ReportProficiencyLevel[];
}

/** The proficiency-levels legend below the matrix: one icon + label per level, from the same
 * `levels` the report response carries — so the legend always matches what the matrix's pills and
 * icons actually show. It is a row, and a row is read left to right, so it ends on the best
 * level. */
export const LevelLegend = ({ levels }: LevelLegendProps) => {
    const { t } = useAmelioTranslations();
    const readingOrder = orderProficiencyLevelsWorstFirst(levels);

    return (
        <Card
            glass={'sm'}
            borderRadius={'2xl'}
            dataTestId={'competency-matrix-legend'}
        >
            <p className={REPORT_SECTION_TITLE}>{t('reports.competencyMatrix.legend.title')}</p>

            <div className={'mt-4 flex flex-wrap gap-6'}>
                {readingOrder.map((level) => (
                    <ProficiencyLegendItem
                        key={level.id}
                        iconName={level.icon}
                        // The wire says "no icon file"/"no color" with `null`; the legend item,
                        // shared with the performance profile, spells the same absence `undefined`.
                        iconUrl={level.iconUrl ?? undefined}
                        iconColor={level.iconColor ?? undefined}
                        label={getLocalizedText(level.label)}
                    />
                ))}
            </div>
        </Card>
    );
};
